import type { DismissRuleSuggestion } from "@reviewer73/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { toast } from "sonner";
import { api } from "../api/client.js";
import { Markdown } from "../components/markdown.js";
import { EmptyNote } from "../components/empty-note.js";
import { Rack } from "../components/rack.js";
import { RuleVotes } from "../components/rule-votes.js";
import { SeverityBadge } from "../components/severity-badge.js";
import { StaggerContainer, StaggerItem } from "../components/stagger.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { formatDate, repoName } from "../lib/format.js";

/** Severity order for the strip listing: loudest first. */
const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 } as const;

export function Rules() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const { data, isLoading, error } = useQuery({ queryKey: ["rules"], queryFn: api.rules });
  const observations = useQuery({ queryKey: ["observations"], queryFn: api.observations });

  const sync = useMutation({
    mutationFn: api.syncRules,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["rules"] });
      if (result.errors.length > 0) toast.error("Sync fehlgeschlagen, alter Stand bleibt aktiv");
      else toast.success(`Sync erfolgreich: ${result.ruleCount} Regel(n)`);
    },
  });

  if (error) {
    return (
      <div className="tile p-6 text-sm text-muted-foreground">
        Regeln konnten nicht geladen werden.
      </div>
    );
  }
  if (isLoading || !data) return <EmptyNote>Regelwerk wird aufgelöst…</EmptyNote>;

  const q = search.trim().toLowerCase();
  const visible = data.rules
    .filter((r) => severity === "all" || r.severity === severity)
    .filter(
      (r) =>
        q === "" ||
        r.id.toLowerCase().includes(q) ||
        r.title.toLowerCase().includes(q) ||
        r.group.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)),
    )
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.id.localeCompare(b.id));
  const groups = [...new Set(visible.map((r) => r.group))].sort();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-bold tracking-tight">Regelwerk</h1>
        <Button disabled={sync.isPending} onClick={() => sync.mutate()}>
          {sync.isPending ? "synct…" : "Sync auslösen"}
        </Button>
      </div>

      {/* The sync placard: one printed line, loud only when it failed. */}
      <div className="tile p-4 text-sm">
        {data.syncStatus.syncedAt ? (
          <p>
            Letzter Sync {formatDate(data.syncStatus.syncedAt)} ·{" "}
            <span className="font-mono tabular-nums">{data.syncStatus.ruleCount}</span> Regel(n) ·
            Gruppen: {data.syncStatus.groups.join(", ") || "keine"}
          </p>
        ) : (
          <p className="text-muted-foreground">Noch kein Sync gelaufen.</p>
        )}
        {data.syncStatus.errors.length > 0 ? (
          <div className="mt-3 border-t border-border pt-3">
            <p className="flex items-center gap-2 font-bold">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{
                  color: "var(--sev-critical)",
                  background: "color-mix(in srgb, var(--sev-critical) 10%, var(--background))",
                }}
              >
                Sync-Fehler
              </span>
              Der letzte Sync ist fehlgeschlagen. Der vorherige Stand bleibt aktiv.
            </p>
            <ul className="mt-2 list-inside list-disc font-mono text-xs text-muted-foreground">
              {data.syncStatus.errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      <Tabs defaultValue="rules">
        <TabsList>
          <TabsTrigger value="rules">Regeln</TabsTrigger>
          <TabsTrigger value="suggestions">
            Vorschläge
            {observations.data && observations.data.length > 0 ? (
              <span className="ml-1.5 tabular-nums opacity-70">{observations.data.length}</span>
            ) : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rules" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Regel, Gruppe, Tag…"
              className="w-56"
            />
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">alle Severities</SelectItem>
                <SelectItem value="critical">kritisch</SelectItem>
                <SelectItem value="high">hoch</SelectItem>
                <SelectItem value="medium">mittel</SelectItem>
                <SelectItem value="low">niedrig</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs tabular-nums text-muted-foreground">
              {visible.length} von {data.rules.length}
            </span>
          </div>

          {groups.length === 0 ? (
            <div className="tile">
              <EmptyNote>
                {data.rules.length === 0
                  ? "Keine Regeln im Rules-Repo gefunden."
                  : "Keine Regel passt zu Suche und Filter."}
              </EmptyNote>
            </div>
          ) : (
            groups.map((group) => (
              <Rack key={group} label={group} count={visible.filter((r) => r.group === group).length}>
                <StaggerContainer className="space-y-2">
                  {visible
                    .filter((r) => r.group === group)
                    .map((rule) => (
                      <StaggerItem key={rule.id}>
                        {/* Each rule is a strip; opening it unfolds the printed
                            prose body below the head line. */}
                        <div className="tile">
                          <div className="flex items-center">
                          <button
                            type="button"
                            onClick={() => setOpen(open === rule.id ? null : rule.id)}
                            className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1 px-4 py-2.5 text-left transition-colors hover:bg-muted/60"
                            aria-expanded={open === rule.id}
                          >
                            <SeverityBadge severity={rule.severity} />
                            <span className="font-mono text-xs text-muted-foreground">{rule.id}</span>
                            <span className="min-w-0 text-sm">{rule.title}</span>
                            {rule.engine === "gitleaks" ? (
                              <Badge variant="secondary" title="Deterministischer Scanner, nicht im LLM-Prompt">
                                gitleaks
                              </Badge>
                            ) : null}
                            {!rule.enabled ? <Badge variant="outline">aus</Badge> : null}
                            <span className="ml-auto flex shrink-0 items-center gap-3 text-xs tabular-nums text-muted-foreground">
                              <RuleVotes votes={rule.votes} />
                              <span>{rule.activeInProjects} Projekt(e)</span>
                            </span>
                          </button>
                          {data.syncStatus.repoWebUrl ? (
                            <a
                              href={`${data.syncStatus.repoWebUrl}/-/blob/${data.syncStatus.repoRef}/${rule.filePath}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Regel in GitLab öffnen"
                              aria-label="Regel in GitLab öffnen"
                              className="mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            >
                              <ExternalLink className="size-4" />
                            </a>
                          ) : null}
                          </div>

                          <AnimatePresence initial={false}>
                            {open === rule.id ? (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                                className="overflow-hidden"
                              >
                                <div className="space-y-2 border-t border-border px-4 py-3">
                                  <p className="text-xs text-muted-foreground">
                                    scope: {rule.scope.length > 0 ? rule.scope.join(", ") : "alle Dateien"}
                                    {rule.tags.length > 0 ? ` · tags: ${rule.tags.join(", ")}` : ""}
                                  </p>
                                  <Markdown>{rule.body}</Markdown>
                                  <p className="text-xs text-muted-foreground">
                                    Änderungen laufen über das Rules-Repo: {rule.filePath}
                                  </p>
                                </div>
                              </motion.div>
                            ) : null}
                          </AnimatePresence>
                        </div>
                      </StaggerItem>
                    ))}
                </StaggerContainer>
              </Rack>
            ))
          )}
        </TabsContent>

        <TabsContent value="suggestions" className="mt-4">
          <RuleSuggestions />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RuleSuggestions() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["observations"], queryFn: api.observations });

  // A verdict lands on the suggestion, not on the observations under it, so the
  // same missing rule does not have to be judged again after the next review.
  // No reason is asked for: unlike a secret, this is triage.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["observations"] });
    // The dashboard's Regelvorschläge tile counts the same open suggestions.
    void queryClient.invalidateQueries({ queryKey: ["stats"] });
  };

  const dismiss = useMutation({
    mutationFn: (body: DismissRuleSuggestion) => api.dismissRuleSuggestion(body),
    onSuccess: (_data, body) => {
      invalidate();
      // There is no "ignoriert"-Filter to fish it back out of, so the undo has to
      // travel with the confirmation.
      toast.success(body.kind === "resolved" ? "Vorschlag erledigt" : "Vorschlag ignoriert", {
        action: {
          label: "Rückgängig",
          onClick: () => {
            void api.restoreRuleSuggestion(body.suggestion).then(invalidate);
          },
        },
      });
    },
    onError: (err) => toast.error("Urteil fehlgeschlagen", { description: (err as Error).message }),
  });

  if (error) {
    return (
      <div className="tile">
        <EmptyNote>Beobachtungen konnten nicht geladen werden.</EmptyNote>
      </div>
    );
  }
  if (isLoading || !data) {
    return <EmptyNote>Beobachtungen werden geladen…</EmptyNote>;
  }
  if (data.length === 0) {
    return (
      <div className="tile">
        <EmptyNote>Keine Beobachtungen ohne Regel — nichts zu sichten.</EmptyNote>
      </div>
    );
  }

  const groups = new Map<string, typeof data>();
  for (const o of data) {
    const key = o.suggestedRule ?? "ohne Vorschlag";
    groups.set(key, [...(groups.get(key) ?? []), o]);
  }
  const sortedGroups = [...groups.entries()].sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="space-y-4">
      <p className="max-w-prose text-xs text-muted-foreground">
        Beobachtungen ohne passende Regel, aus Reviews — nie inline kommentiert, nie in der
        Findings-Statistik. Kandidaten für das Rules-Repo. „Erledigt“ heißt: die Regel steht im
        Rules-Repo. „Ignorieren“ heißt: wird keine Regel. Beides nimmt den Vorschlag dauerhaft von
        dieser Liste, auch wenn ein späteres Review ihn erneut macht.
      </p>
      {sortedGroups.map(([suggestedRule, items]) => (
        <Rack
          key={suggestedRule}
          label={suggestedRule}
          count={items.length}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={dismiss.isPending}
                onClick={() =>
                  dismiss.mutate({ suggestion: items[0]!.suggestedRule ?? "", kind: "resolved" })
                }
              >
                erledigt
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={dismiss.isPending}
                onClick={() =>
                  dismiss.mutate({ suggestion: items[0]!.suggestedRule ?? "", kind: "ignored" })
                }
              >
                ignorieren
              </Button>
            </>
          }
        >
          <div className="tile">
            {items.map((o, index) => (
              <div
                key={`${o.projectPath}:${o.mrIid}:${o.file}:${o.line}:${index}`}
                className="border-b border-border px-4 py-2.5 text-sm last:border-0"
              >
                <p className="font-mono text-xs text-muted-foreground">
                  {repoName(o.projectPath)} · MR!{o.mrIid} · {o.file}:{o.line}
                </p>
                <p className="mt-1">{o.message}</p>
              </div>
            ))}
          </div>
        </Rack>
      ))}
    </div>
  );
}
